import {
  parse,
  visit,
  print,
  Kind,
  DocumentNode,
  FragmentDefinitionNode,
  InlineFragmentNode,
} from 'graphql';

/**
 * Inlines all fragment spreads in a GraphQL query string by replacing them with inline fragments.
 * This is a workaround for gqlgen backend limitation that fails to serialize unions in fragments
 * within subscriptions.
 *
 * @param queryString - The GraphQL query string potentially containing fragment definitions
 * @returns The query string with all fragments inlined
 *
 * @example
 * Input:
 * ```
 * subscription { user { ...UserFields } }
 * fragment UserFields on User { id name }
 * ```
 *
 * Output:
 * ```
 * subscription { user { ... on User { id name } } }
 * ```
 */
export function inlineFragments(queryString: string): string {
  try {
    // Parse the query string into an AST
    const document: DocumentNode = parse(queryString);

    // Build a map of fragment definitions for quick lookup
    const fragmentMap = new Map<string, FragmentDefinitionNode>();
    for (const definition of document.definitions) {
      if (definition.kind === Kind.FRAGMENT_DEFINITION) {
        fragmentMap.set(definition.name.value, definition);
      }
    }

    // If there are no fragments, return the original query
    if (fragmentMap.size === 0) {
      return queryString;
    }

    // Visit the AST and replace fragment spreads with inline fragments
    const transformedDocument = visit(document, {
      // Replace fragment spreads with inline fragments
      FragmentSpread(node) {
        const fragmentDef = fragmentMap.get(node.name.value);
        if (!fragmentDef) {
          // Fragment not found, leave as is (shouldn't happen in valid queries)
          return undefined;
        }

        // Create an inline fragment from the fragment definition
        const inlineFragment: InlineFragmentNode = {
          kind: Kind.INLINE_FRAGMENT,
          typeCondition: fragmentDef.typeCondition,
          selectionSet: fragmentDef.selectionSet,
          directives: [
            ...(node.directives || []),
            ...(fragmentDef.directives || []),
          ],
        };

        return inlineFragment;
      },
      // Remove fragment definitions from the document
      FragmentDefinition() {
        return null;
      },
    });

    // Convert the transformed AST back to a string
    return print(transformedDocument);
  } catch (error) {
    // If parsing fails, return the original string
    console.error('Failed to inline fragments:', error);
    return queryString;
  }
}
